import { computePosition, offset, flip, shift, arrow, type Placement } from '@floating-ui/dom'

const CORRAL_ID = 'TooltipCorral'
const FADE_MS = 150

export type TooltipState = { element: HTMLDivElement; onUnmount: () => void }
let currentTooltip: TooltipState | null = null
let hideTimeout: ReturnType<typeof setTimeout> | null = null
let currentStopPositionLoop: (() => void) | null = null

function getCorral(): HTMLElement {
  const corral = document.getElementById(CORRAL_ID)
  if (!corral) throw new Error(`TooltipCorral (#${CORRAL_ID}) not found`)
  return corral
}

function removeTooltip(state: TooltipState): void {
  state.onUnmount()
  state.element.remove()
  if (currentTooltip === state) currentTooltip = null
}

export function createTooltip(onUnmount: () => void): {
  tooltipEl: HTMLDivElement
  contentEl: HTMLDivElement
  arrowEl: HTMLDivElement
} {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
  currentStopPositionLoop?.()
  currentStopPositionLoop = null
  if (currentTooltip) {
    removeTooltip(currentTooltip)
  }
  const corral = getCorral()
  const tooltipEl = document.createElement('div')
  tooltipEl.className = 'Tooltip'
  tooltipEl.setAttribute('role', 'tooltip')
  tooltipEl.id = `tooltip-${crypto.randomUUID()}`
  tooltipEl.setAttribute('aria-hidden', 'true')
  const arrowElement = document.createElement('div')
  arrowElement.className = 'Arrow'
  const contentElement = document.createElement('div')
  contentElement.className = 'Content'
  tooltipEl.appendChild(contentElement)
  tooltipEl.appendChild(arrowElement)
  corral.appendChild(tooltipEl)
  currentTooltip = { element: tooltipEl, onUnmount }
  return { tooltipEl: tooltipEl, contentEl: contentElement, arrowEl: arrowElement }
}

async function updateTooltipPosition(
  reference: Element,
  floating: HTMLElement,
  arrowEl: HTMLDivElement,
  placement: Placement,
): Promise<void> {
  const {
    x,
    y,
    placement: resolvedPlacement,
    middlewareData,
  } = await computePosition(reference, floating, {
    placement,
    strategy: 'fixed',
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 10 }),
      arrow({ element: arrowEl }),
    ],
  })
  Object.assign(floating.style, { left: `${x}px`, top: `${y}px` })
  if (middlewareData.arrow) {
    const { x: ax, y: ay } = middlewareData.arrow
    const isTopBottom = resolvedPlacement.startsWith('top') || resolvedPlacement.startsWith('bottom')
    arrowEl.style.left = isTopBottom && ax !== undefined ? `${ax}px` : ''
    arrowEl.style.top = !isTopBottom && ay !== undefined ? `${ay}px` : ''
  }
  arrowEl.setAttribute('data-placement', resolvedPlacement)
}

export async function positionAndShowTooltip(
  reference: Element,
  floating: HTMLElement,
  arrowEl: HTMLDivElement,
  placement: Placement = 'top',
): Promise<() => void> {
  await updateTooltipPosition(reference, floating, arrowEl, placement)
  Object.assign(floating.style, { opacity: '1', visibility: 'visible' })
  floating.setAttribute('aria-hidden', 'false')
  const intervalId = setInterval(() => {
    updateTooltipPosition(reference, floating, arrowEl, placement)
  }, 10)
  const stop = () => {
    clearInterval(intervalId)
    if (currentStopPositionLoop === stop) currentStopPositionLoop = null
  }
  currentStopPositionLoop = stop
  return stop
}

export function scheduleTooltipHide(
  state: TooltipState,
  delayBeforeFadeMs = 1000,
): void {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
  hideTimeout = setTimeout(() => {
    hideTimeout = null
    state.element.setAttribute('aria-hidden', 'true')
    state.element.style.opacity = '0'
    setTimeout(() => removeTooltip(state), FADE_MS)
  }, delayBeforeFadeMs)
}
